import SolutionKitService from '@baserow/modules/core/services/solutionKit'

export const state = () => ({
  catalog: [],
  catalogLoading: false,
  catalogAttempted: false,
  catalogInitialized: false,
  catalogError: null,
  workspaceKitsById: {},
  installJobsById: {},
  kitWorkspacesById: {},
})

function normalizeWorkspaceId(workspaceId) {
  const id = Number(workspaceId)
  return Number.isFinite(id) ? id : null
}

export const mutations = {
  SET_CATALOG_LOADING(state, value) {
    state.catalogLoading = value
  },
  SET_CATALOG_ATTEMPTED(state, value) {
    state.catalogAttempted = value
  },
  SET_CATALOG_INITIALIZED(state, value) {
    state.catalogInitialized = value
  },
  SET_CATALOG_ERROR(state, error) {
    state.catalogError = error
  },
  SET_CATALOG(state, kits) {
    state.catalog = kits
  },
  SET_WORKSPACE_KITS(state, { workspaceId, installations }) {
    const wsId = normalizeWorkspaceId(workspaceId)
    if (wsId === null) {
      return
    }
    state.workspaceKitsById = {
      ...state.workspaceKitsById,
      [wsId]: {
        installedKits: installations,
      },
    }
  },
  REGISTER_INSTALL_JOB(
    state,
    { jobId, workspaceId, kitName = '', notifyVokalis = false }
  ) {
    const wsId = normalizeWorkspaceId(workspaceId)
    if (wsId === null) {
      return
    }
    state.installJobsById = {
      ...state.installJobsById,
      [jobId]: {
        workspaceId: wsId,
        kitName,
        notifyVokalis,
      },
    }
  },
  CLEAR_INSTALL_JOB(state, jobId) {
    const { [jobId]: _removed, ...rest } = state.installJobsById
    state.installJobsById = rest
  },
  SET_KIT_WORKSPACES(state, { kitId, workspaces }) {
    state.kitWorkspacesById = {
      ...state.kitWorkspacesById,
      [kitId]: workspaces,
    }
  },
}

export const actions = {
  async fetchCatalog({ commit }) {
    commit('SET_CATALOG_ATTEMPTED', true)
    commit('SET_CATALOG_LOADING', true)
    commit('SET_CATALOG_ERROR', null)
    try {
      const { data } = await SolutionKitService(this.$client).listKits()
      commit('SET_CATALOG', data)
      commit('SET_CATALOG_INITIALIZED', true)
    } catch (error) {
      commit('SET_CATALOG', [])
      commit('SET_CATALOG_ERROR', error)
    } finally {
      commit('SET_CATALOG_LOADING', false)
    }
  },
  async installKit(
    { rootGetters },
    { kit, workspaceId, workspaceName = null, notifyVokalis = false }
  ) {
    const userWebSocketId = rootGetters['auth/webSocketId']
    const { data } = await SolutionKitService(this.$client).deployKit(kit.id, {
      workspaceId,
      workspaceName,
      userWebSocketId,
      notifyVokalis,
    })
    return data
  },
  async fetchWorkspaceKits({ commit }, workspaceId) {
    const wsId = normalizeWorkspaceId(workspaceId)
    if (wsId === null) {
      return
    }
    const { data } = await SolutionKitService(
      this.$client
    ).listWorkspaceInstallations(wsId)
    commit('SET_WORKSPACE_KITS', {
      workspaceId: wsId,
      installations: data,
    })
  },
  async fetchKitWorkspaces({ commit }, kitId) {
    const { data } = await SolutionKitService(this.$client).listKitWorkspaces(
      kitId
    )
    commit('SET_KIT_WORKSPACES', { kitId, workspaces: data })
  },
  async uninstallInstallation(
    { dispatch, rootGetters },
    { installationId, workspaceId }
  ) {
    const { data } = await SolutionKitService(
      this.$client
    ).uninstallInstallation(installationId)

    const deletedApps = (data && data.deleted_applications) || []
    if (Array.isArray(deletedApps) && deletedApps.length) {
      const getApplicationById = rootGetters['application/get']
      deletedApps.forEach((entry) => {
        const appId =
          entry &&
          (entry.database_id ||
            entry.builder_id ||
            entry.dashboard_id ||
            entry.automation_id)
        if (!appId || typeof getApplicationById !== 'function') {
          return
        }
        const application = getApplicationById(appId)
        if (application) {
          dispatch('application/forceDelete', application, { root: true })
        }
      })
    }

    if (workspaceId) {
      try {
        await dispatch('fetchWorkspaceKits', workspaceId)
      } catch (error) {
        // Uninstall already succeeded server-side.
      }
    }
    try {
      await dispatch('application/fetchAll', null, { root: true })
    } catch (error) {
      // Best-effort sidebar refresh.
    }
    const wsId = normalizeWorkspaceId(workspaceId)
    if (wsId !== null && typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('workspace-menu-entries-updated', {
          detail: { workspaceId: wsId },
        })
      )
    }
    return data
  },
}

export const getters = {
  getCatalog(state) {
    return state.catalog
  },
  isCatalogLoading(state) {
    return state.catalogLoading
  },
  isCatalogAttempted(state) {
    return state.catalogAttempted
  },
  isCatalogInitialized(state) {
    return state.catalogInitialized
  },
  getCatalogError(state) {
    return state.catalogError
  },
  getInstalledKitsForWorkspace(state) {
    return (workspaceId) => {
      const wsId = normalizeWorkspaceId(workspaceId)
      const entry = wsId !== null ? state.workspaceKitsById[wsId] : null
      if (!entry) {
        return []
      }
      return entry.installedKits.filter(
        (installation) => installation.status === 'succeeded'
      )
    }
  },
  isKitInstalledInWorkspace(state) {
    return (workspaceId, kitId) => {
      const wsId = normalizeWorkspaceId(workspaceId)
      const entry = wsId !== null ? state.workspaceKitsById[wsId] : null
      if (!entry) {
        return false
      }
      return entry.installedKits.some(
        (installation) =>
          installation.kit &&
          installation.kit.id === kitId &&
          installation.status === 'succeeded'
      )
    }
  },
  getKitWorkspaces(state) {
    return (kitId) => state.kitWorkspacesById[kitId] || []
  },
  getWorkspaceIdForJob(state) {
    return (jobId) => {
      const entry = state.installJobsById[jobId]
      return entry ? entry.workspaceId : null
    }
  },
  getInstallJobMeta(state) {
    return (jobId) => state.installJobsById[jobId] || null
  },
  hasInstallJobForWorkspace(state) {
    return (workspaceId) => {
      const wsId = normalizeWorkspaceId(workspaceId)
      if (wsId === null) {
        return false
      }
      return Object.values(state.installJobsById).some(
        (entry) => entry.workspaceId === wsId
      )
    }
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
